import {
  useModalContext,
  useWSSnackbar,
  WSAmountInput,
  WSButton,
  WSContainer,
  WSForm,
  WSGrid,
  WSIcon,
  WSLayout,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";
import React from "react";
import { useHistory } from "react-router-dom";
import { WSQueries } from "../../query/WSQuery";
import { formatMoney } from "accounting";
import { useWithholdingBalance } from "../../query/taxes/queries";
import moment from "moment";
import { useBrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import {
  getCurrentIRSQuarter,
  getCurrentIRSYear,
  getIRSQuarters
} from "../utils/getIRSQuarter";
import { useActivities, useMemberProfile } from "../../query/users/queries";
import { validatorAmount } from "../../utils/validators";
import { useUserId } from "../../query/hooks/helpers";
import { MODAL_ADD_SSN, TaxesModalAddSSN } from "./TaxesModalAddSSN";
import {
  MODAL_CONSENT_EFTPS,
  TaxesModalConsentEFTPS
} from "./TaxesModalConsentEFTPS";
import { WithholdingTransactionType } from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import { Currency } from "@wingspanhq/users/dist/lib/interfaces";
import { useCreatePendingWithholding } from "../../query/taxes/mutations";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import { TaxQuarter } from "@wingspanhq/bookkeeping/dist/lib/interfaces/tax";
import { CurrencyUSD } from "../../services/bookkeeping";

const currentYear = getCurrentIRSYear();
const currentQuarter = getCurrentIRSQuarter();
const QUARTERS_OPTIONS = getIRSQuarters(currentYear).map(q => ({
  value: `Q${q.quarter} ${currentYear}`,
  label: `Q${q.quarter} ${currentYear} ${
    currentQuarter.quarter === q.quarter ? "(Current quarter)" : ""
  }`
}));

export const getWSTaxQuarter = () => {
  const quarter = getCurrentIRSQuarter().quarter;
  let wsCurrentQuarter: TaxQuarter;

  switch (quarter) {
    case 1:
      wsCurrentQuarter = TaxQuarter.q1;
      break;
    case 2:
      wsCurrentQuarter = TaxQuarter.q2;
      break;
    case 3:
      wsCurrentQuarter = TaxQuarter.q3;
      break;
    default:
      wsCurrentQuarter = TaxQuarter.q4;
  }

  return wsCurrentQuarter;
};

export const TaxesSendToIRS: React.FC = () => {
  const history = useHistory();
  const memberId = useUserId();
  const { openSnackbar } = useWSSnackbar();
  const qMemberProfile = useMemberProfile(memberId);
  const qActivity = useActivities(memberId);
  const qWithholdingBalance = useWithholdingBalance();
  const { openModal } = useModalContext();
  const qFeatureFlags = useFeatureFlags();
  const [
    createPendingWithholding,
    createPendingWithholdingMeta
  ] = useCreatePendingWithholding();

  useBrowserPageTitle("Taxes - Pay Federal Taxes", "");

  return (
    <WSQueries
      queries={{
        balance: qWithholdingBalance,
        member: qMemberProfile,
        qActivity,
        qFeatureFlags
      }}
      renderLoader={() => (
        <WSLayout
          headerLeft={
            <WSIcon
              name="chevronLeft"
              size="M"
              color="grey600"
              onClick={() => history.goBack()}
            />
          }
          line
        />
      )}
    >
      {({ balance, member, qActivity }) => {
        const withholdingBalance = Math.abs(balance.data.taxWithholdingBalance);

        return (
          <WSForm
            onSubmit={async formData => {
              if (!member.data.profile.ssnProvided) {
                await new Promise((onSuccess, onFail) =>
                  openModal(MODAL_ADD_SSN, { onSuccess, onFail })
                );
              }

              if (!qActivity.data.events.eftpsEnrolledAt) {
                await new Promise((onSuccess, onFail) =>
                  openModal(MODAL_CONSENT_EFTPS, { onSuccess, onFail })
                );
              }

              await createPendingWithholding(
                {
                  amount: formData.amount,
                  type: WithholdingTransactionType.TaxPayment,
                  currency: CurrencyUSD,
                  quarter: getWSTaxQuarter(),
                  year: currentYear
                },
                {
                  throwOnError: true
                }
              );

              openSnackbar({
                message: "Successfully sent!",
                duration: 3000,
                type: "success"
              });

              history.push("/member/taxes/dashboard");
            }}
            defaultValues={{
              amount: (undefined as any) as number,
              quarter: `Q${currentQuarter.quarter} ${currentYear}`
            }}
            validationSchema={Yup.object().shape({
              amount: validatorAmount(withholdingBalance),
              quarter: Yup.string().required("Quarter is required")
            })}
          >
            <TaxesModalAddSSN />
            <TaxesModalConsentEFTPS />
            <WSLayout
              headerLeft={
                <WSIcon
                  name="chevronLeft"
                  size="M"
                  color="grey600"
                  onClick={() => history.goBack()}
                />
              }
              headerCenter={
                <WSText.Heading4>
                  Send payment to IRS - Q{currentQuarter.quarter} {currentYear}
                </WSText.Heading4>
              }
              line
            >
              <WSContainer verticalPadding>
                <WSText.Paragraph mt="S" mb="L" align="center">
                  We automatically directly submit to the IRS.
                </WSText.Paragraph>

                <WSGrid.Centered XS={6} S={8} M={6} L={6} XL={4}>
                  <WSForm.Field
                    name="amount"
                    mb="M"
                    component={WSAmountInput}
                  />
                  <WSText.Label align="center" color="grey600">
                    {formatMoney(balance.data.taxWithholdingBalance * -1)}{" "}
                    available
                  </WSText.Label>

                  <WSForm.Field
                    name="quarter"
                    mb="M"
                    label="Time period"
                    hidden={true}
                    component={WSSelect}
                    componentProps={{
                      options: QUARTERS_OPTIONS
                    }}
                  />
                </WSGrid.Centered>

                <WSGrid.Centered XS={6} S={8} M={6} L={6} XL={4}>
                  <WSErrorMessage
                    mb="M"
                    error={createPendingWithholdingMeta.error}
                    contextKey="PendingWithholding"
                  />

                  <WSButton.Primary
                    fullWidth
                    mt="L"
                    type="submit"
                    data-testid="submitSendToIRS"
                  >
                    Send request
                  </WSButton.Primary>
                  <WSButton.Tertiary
                    fullWidth
                    mt="S"
                    mb="XL"
                    type="button"
                    onClick={() => history.goBack()}
                  >
                    Cancel
                  </WSButton.Tertiary>
                </WSGrid.Centered>
              </WSContainer>
            </WSLayout>
          </WSForm>
        );
      }}
    </WSQueries>
  );
};
